/**
 * Normalizes a URL by removing query parameters and trailing slashes for comparison
 * @param url - The URL to normalize
 * @returns The normalized URL
 */
export function normalizeUrl(url: string): string {
  // Remove query parameters
  const urlWithoutQuery = url.split('?')[0];
  // Remove trailing slash, but keep root slash
  return urlWithoutQuery.replace(/\/$/, '') || '/';
}

/**
 * Checks if the current URL matches the target URL (prevents same-URL navigation)
 * @param targetUrl - The URL to navigate to
 * @param currentUrl - The current URL (optional, defaults to window.location.pathname)
 * @returns true if the URLs match (navigation should be prevented), false otherwise
 */
export function shouldPreventNavigation(targetUrl: string, currentUrl?: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const current = currentUrl || window.location.pathname;
  const normalizedCurrent = normalizeUrl(current);
  const normalizedTarget = normalizeUrl(targetUrl);

  return normalizedCurrent === normalizedTarget;
}

/**
 * Creates an onClick handler that prevents navigation to the same URL
 * @param targetUrl - The URL to navigate to
 * @param currentUrl - The current URL (optional, defaults to window.location.pathname)
 * @param additionalHandler - Additional onClick handler to call
 * @returns An onClick handler function
 */
export function createNavigationHandler(
  targetUrl: string,
  currentUrl?: string,
  additionalHandler?: (e: React.MouseEvent) => void
) {
  return (e: React.MouseEvent) => {
    // Call additional handler first if provided
    if (additionalHandler) {
      additionalHandler(e);
    }

    // Prevent navigation if we're already on the target URL
    if (shouldPreventNavigation(targetUrl, currentUrl)) {
      e.preventDefault();
      console.log('Preventing navigation to same URL:', {
        currentUrl: currentUrl || window.location.pathname,
        targetUrl,
        normalizedCurrent: normalizeUrl(currentUrl || window.location.pathname),
        normalizedTarget: normalizeUrl(targetUrl)
      });
    }
  };
}

/**
 * Safely navigates to a URL using router.push when available, with fallback to window.location.href
 * @param router - Next.js router instance
 * @param url - The URL to navigate to
 */
export function safeNavigate(
  router: { isReady: boolean; push: (url: string) => void },
  url: string
) {
  if (router.isReady) {
    router.push(url);
  } else {
    // Fallback to window.location.href only if router is not ready
    window.location.href = url;
  }
}
